"use client"
import { useState, useEffect } from "react"
import { Button } from "../../components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover"
import { getTempToken } from "../../api/authService"
import { Wand2, Search } from "lucide-react"
import { Input } from "../../components/ui/input"

interface EmailVariable {
  name: string
  label: string
  example: string
  category: string
}

interface VariableInsertionPanelProps {
  onVariableSelect: (variableName: string) => void
}

export function VariableInsertionPanel({ onVariableSelect }: VariableInsertionPanelProps) {
  const [variables, setVariables] = useState<EmailVariable[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (open && variables.length === 0) {
      fetchVariables()
    }
  }, [open])

  const fetchVariables = async () => {
    try {
      setLoading(true)
      const token = await getTempToken()
      const res = await fetch("https://tsoministries.com/wp-json/custom-api/v1/variables", {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (res.ok) {
        const data = await res.json()
        setVariables(data)
      }
    } catch (error) {
      console.error("[v0] Error fetching variables:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleVariableClick = (variable: EmailVariable) => {
    onVariableSelect(variable.name)
    setSearchQuery("")
  }

  // Group variables by category
  const groupedVariables = variables.reduce(
    (acc, variable) => {
      if (!acc[variable.category]) acc[variable.category] = []
      acc[variable.category].push(variable)
      return acc
    },
    {} as { [key: string]: EmailVariable[] },
  )

  // Filter based on search query
  const filteredGroups = Object.entries(groupedVariables).reduce(
    (acc, [category, vars]) => {
      const filtered = vars.filter(
        (v) =>
          v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          v.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      if (filtered.length > 0) {
        acc[category] = filtered
      }
      return acc
    },
    {} as { [key: string]: EmailVariable[] },
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 bg-transparent">
          <Wand2 className="h-4 w-4" />
          Insert Variable
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">Email Variables</h3>
            <p className="text-xs text-gray-500 mb-3">Type @ in editor to autocomplete, or select a variable below</p>
          </div>

          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search variables..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>

          {loading ? (
            <div className="text-center py-4 text-sm text-gray-500">Loading variables...</div>
          ) : Object.keys(filteredGroups).length === 0 ? (
            <div className="text-center py-4 text-sm text-gray-500">No variables found</div>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {Object.entries(filteredGroups).map(([category, vars]) => (
                <div key={category}>
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{category}</h4>
                  <div className="space-y-1">
                    {vars.map((variable) => (
                      <button
                        key={variable.name}
                        onClick={() => handleVariableClick(variable)}
                        className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-sm font-medium text-blue-600 truncate">
                              [@{variable.name}]
                            </div>
                            <div className="text-xs text-gray-600">{variable.label}</div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Example: {variable.example}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t pt-3 text-xs text-gray-600">
            <p className="font-semibold mb-2">Quick Tip:</p>
            <p>Type @ in the editor to trigger autocomplete and quickly find variables.</p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
